/**
 * Parameter model — a 1:1 mapping of `pixeloe.torch.pixelize.pixelize()`'s
 * keyword arguments, plus the target-size controls the Gradio demo exposes.
 */

export const DOWNSAMPLE_MODES = [
  'contrast',
  'k_centroid',
  'lanczos',
  'bicubic',
  'bilinear',
  'nearest',
  'area',
] as const;
export type DownsampleMode = (typeof DOWNSAMPLE_MODES)[number];

export const SHARPEN_MODES = ['none', 'unsharp', 'laplacian'] as const;
export type SharpenMode = (typeof SHARPEN_MODES)[number];

export const QUANT_MODES = ['kmeans', 'weighted-kmeans', 'repeat-kmeans'] as const;
export type QuantMode = (typeof QUANT_MODES)[number];

export const DITHER_MODES = ['ordered', 'error_diffusion', 'none'] as const;
export type DitherMode = (typeof DITHER_MODES)[number];

export interface Params {
  /** Output width in pixels (`target_w`). */
  targetW: number;
  /** Output height in pixels (`target_h`). */
  targetH: number;
  /** Keep targetW/targetH locked to the source aspect ratio. */
  bind: boolean;
  /** `pixel_size` — how many source pixels make up one output pixel. */
  pixelSize: number;
  /** `thickness` — outline expansion strength, 0 disables it. */
  thickness: number;
  /** `mode` — downscale algorithm. */
  mode: DownsampleMode;
  /** `sharpen_mode` — applied right after outline expansion. */
  sharpenMode: SharpenMode;
  /** `do_color_match`. */
  doColorMatch: boolean;
  /** `do_quant`. */
  doQuant: boolean;
  /** `num_colors`. */
  numColors: number;
  /** `quant_mode`. */
  quantMode: QuantMode;
  /** `dither_mode`. */
  ditherMode: DitherMode;
  /** `no_post_upscale` — return the small image instead of the upscaled one. */
  noPostUpscale: boolean;
}

/** Defaults mirror `client/demo-gr.py`. */
export const DEFAULT_PARAMS: Params = {
  targetW: 256,
  targetH: 256,
  bind: true,
  pixelSize: 4,
  thickness: 3,
  mode: 'contrast',
  sharpenMode: 'none',
  doColorMatch: true,
  doQuant: false,
  numColors: 32,
  quantMode: 'kmeans',
  ditherMode: 'ordered',
  noPostUpscale: false,
};

export const LIMITS = {
  target: { min: 8, max: 1024 },
  pixelSize: { min: 2, max: 8 },
  thickness: { min: 0, max: 6 },
  numColors: { min: 2, max: 256 },
} as const;

function clampInt(v: unknown, min: number, max: number, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function pick<T extends readonly string[]>(
  list: T,
  v: unknown,
  fallback: T[number],
): T[number] {
  return typeof v === 'string' && (list as readonly string[]).includes(v)
    ? (v as T[number])
    : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (v === '1' || v === 1 || v === true || v === 'true') return true;
  if (v === '0' || v === 0 || v === false || v === 'false') return false;
  return fallback;
}

/** Normalise an arbitrary object into a valid {@link Params}. */
export function sanitizeParams(raw: Partial<Record<keyof Params, unknown>>): Params {
  const d = DEFAULT_PARAMS;
  const p: Params = {
    targetW: clampInt(raw.targetW, LIMITS.target.min, LIMITS.target.max, d.targetW),
    targetH: clampInt(raw.targetH, LIMITS.target.min, LIMITS.target.max, d.targetH),
    bind: bool(raw.bind, d.bind),
    pixelSize: clampInt(raw.pixelSize, LIMITS.pixelSize.min, LIMITS.pixelSize.max, d.pixelSize),
    thickness: clampInt(raw.thickness, LIMITS.thickness.min, LIMITS.thickness.max, d.thickness),
    mode: pick(DOWNSAMPLE_MODES, raw.mode, d.mode),
    sharpenMode: pick(SHARPEN_MODES, raw.sharpenMode, d.sharpenMode),
    doColorMatch: bool(raw.doColorMatch, d.doColorMatch),
    doQuant: bool(raw.doQuant, d.doQuant),
    numColors: clampInt(raw.numColors, LIMITS.numColors.min, LIMITS.numColors.max, d.numColors),
    quantMode: pick(QUANT_MODES, raw.quantMode, d.quantMode),
    ditherMode: pick(DITHER_MODES, raw.ditherMode, d.ditherMode),
    noPostUpscale: bool(raw.noPostUpscale, d.noPostUpscale),
  };
  return p;
}

const SCHEMA: Record<keyof Params, string> = {
  targetW: 'w',
  targetH: 'h',
  bind: 'b',
  pixelSize: 'p',
  thickness: 't',
  mode: 'm',
  sharpenMode: 's',
  doColorMatch: 'c',
  doQuant: 'q',
  numColors: 'n',
  quantMode: 'qm',
  ditherMode: 'd',
  noPostUpscale: 'u',
};

const SHORT_MODE: Record<string, DownsampleMode> = {
  c: 'contrast',
  k: 'k_centroid',
  l: 'lanczos',
  b: 'bicubic',
  i: 'bilinear',
  n: 'nearest',
  a: 'area',
};

/** Serialise params into a compact URL hash fragment (without the leading `#`). */
export function encodeParams(p: Params): string {
  const sp = new URLSearchParams();
  for (const key of Object.keys(SCHEMA) as (keyof Params)[]) {
    const v = p[key];
    if (v === DEFAULT_PARAMS[key]) continue;
    const sk = SCHEMA[key];
    if (key === 'mode') {
      const code = Object.entries(SHORT_MODE).find(([, m]) => m === v)?.[0];
      if (code) sp.set(sk, code);
    } else if (typeof v === 'boolean') {
      sp.set(sk, v ? '1' : '0');
    } else {
      sp.set(sk, String(v));
    }
  }
  return sp.toString();
}

/** Parse a URL hash fragment produced by {@link encodeParams}. */
export function decodeParams(hash: string): Params {
  const sp = new URLSearchParams(hash.replace(/^#/, ''));
  const raw: Partial<Record<keyof Params, unknown>> = {};
  for (const key of Object.keys(SCHEMA) as (keyof Params)[]) {
    const sk = SCHEMA[key];
    if (!sp.has(sk)) continue;
    const v = sp.get(sk)!;
    if (key === 'mode') {
      raw.mode = SHORT_MODE[v] ?? v;
    } else {
      raw[key] = v;
    }
  }
  return sanitizeParams(raw);
}

/** Size of the working buffer: `target * pixel_size`. */
export function workingSize(p: Params): { w: number; h: number } {
  return { w: p.targetW * p.pixelSize, h: p.targetH * p.pixelSize };
}

/**
 * Dimensions of the image the reference actually returns.
 *
 * Two quirks stack up here, both reproduced from `pixelize`: the working image
 * is padded by a full `pixel_size` on each axis (because `pad = pixel_size -
 * size % pixel_size` is `pixel_size` rather than zero when the size already
 * divides evenly), so the downscale emits `target + 1`; and the result is then
 * magnified by `nearest-exact` with `scale_factor=pixel_size` unless
 * `no_post_upscale` is set. The output is therefore `(target + 1) * pixel_size`,
 * which is very close to — but not the same as — the working resolution.
 */
export function outputSize(p: Params): { w: number; h: number } {
  const scale = p.noPostUpscale ? 1 : p.pixelSize;
  return { w: (p.targetW + 1) * scale, h: (p.targetH + 1) * scale };
}
